import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Home, CreditCard, Building2, Settings, LogOut, Target, Calendar, DollarSign, TrendingUp } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useDarkMode } from '../hooks/useDarkMode';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { darkMode } = useDarkMode();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navigation = [
    { name: 'Dashboard', href: '/app', icon: Home },
    { name: 'Track', href: '/app/transactions', icon: CreditCard },
    { name: 'Budget', href: '/app/budget', icon: DollarSign },
    { name: 'Plan', href: '/app/goals', icon: Target },
    { name: 'Calendar', href: '/app/calendar', icon: Calendar },
    { name: 'Dividends', href: '/app/dividends', icon: TrendingUp },
    { name: 'Properties', href: '/app/properties', icon: Building2 },
    { name: 'Settings', href: '/app/settings', icon: Settings },
  ];

  // const handleSignOut = async () => {
  //   await signOut();
  // };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-50">
        <nav className="flex justify-around py-2">
          <Link to="/" className="flex flex-col items-center justify-center px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">
            <img
              src={process.env.PUBLIC_URL + '/logo.png'}
              alt="SimpleTracker Logo"
              className="w-6 h-6 rounded-md shadow-sm mb-1"
            />
            <span className="text-xs">Home</span>
          </Link>
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${isActive ? 'active' : ''}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs">{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Desktop navigation */}
      <div
        className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:bg-white dark:lg:bg-gray-800 lg:border-r lg:border-gray-200 dark:lg:border-gray-700 transition-transform duration-300 z-40 ${sidebarOpen ? 'lg:translate-x-0' : 'lg:-translate-x-full'}`}
        style={{ width: 256 }}
      >
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex items-center h-16 flex-shrink-0 px-6 border-b border-gray-200 dark:border-gray-700">
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img
                src={process.env.PUBLIC_URL + '/logo.png'}
                alt="SimpleTracker Logo"
                className="w-9 h-9 rounded-md shadow-sm mr-2"
              />
              <span className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">SimpleTracker</span>
            </Link>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`nav-link ${isActive ? 'active' : ''}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>
          
          {/* Logout Button */}
          <div className="px-4 py-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSignOut}
              className="nav-link w-full text-left"
            >
              <LogOut className="w-5 h-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar open button (desktop only, only when sidebar is closed) */}
      {!sidebarOpen && (
        <button
          type="button"
          className="hidden lg:block fixed top-4 left-4 z-50 bg-white border border-gray-200 rounded-full p-2 shadow hover:bg-gray-100 focus:outline-none transition"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open sidebar"
        >
          <svg
            className="w-6 h-6 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:pl-64' : 'lg:pl-0'}`}>
        <main className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}; 